// hooks/excerpts/queries.js - Excerpt-specific queries and keys
import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient, useQueries, useInfiniteQuery } from '@tanstack/react-query';
import QualAPI from 'utils/QualAPI';
import FilterAPI from 'apis/FilterAPI';
import ExcerptAPI from 'apis/ExcerptAPI';
import * as Sentry from '@sentry/react';

// Define query keys for excerpts
export const excerptKeys = {
  all: ['excerpts'],
  lists: () => [...excerptKeys.all, 'list'],
  list: (transcriptId) => [...excerptKeys.lists(), transcriptId],
  details: () => [...excerptKeys.all, 'detail'],
  detail: (id) => [...excerptKeys.details(), id],
  // Paragraph mappings
  paragraphMappings: () => [...excerptKeys.all, 'paragraph-mappings'],
  paragraphExcerptIds: (codeableId, codeableType = 'Transcript', start, end) => [...excerptKeys.paragraphMappings(), codeableType, codeableId, start, end],
  // Filter-based queries
  filtered: (projectId, filters, source) => [...excerptKeys.all, 'filtered', projectId, filters, source],
  // Filter report
  filterReport: (projectId, filters, source) => [...excerptKeys.all, 'filter-report', projectId, filters, source],
  // Code-specific queries
  codeInfinite: (codeId, sort) => [...excerptKeys.all, 'code-infinite', codeId, sort],
  // Bulk fetch queries
  bulk: (projectId, excerptIds) => [...excerptKeys.all, 'bulk', projectId, excerptIds],
};

// Function-scope use only (module cycle via transcripts/queries — safe
// because nothing touches the binding at module-eval time).
import { seedExcerptDetails } from '../utils/transcriptCacheUtils';

// A membership id with no detail entry is an invariant violation, not a
// runtime state to recover from: detail entries are session-persistent
// (gcTime Infinity in queryClientConfig), and every membership writer
// seeds the entry BEFORE the id (transcriptCacheUtils' ordering
// invariant). A hole therefore means a writer broke the invariant or
// something removed a detail entry out from under a live transcript —
// a bug we want to see, so it is reported to Sentry and the id is
// resolved as absent. Nothing refetches: the self-healing path that used
// to live here masked exactly these bugs and cost rounds of race fixes of
// its own. Optimistic UUID ids are skipped — the create mutation writes
// their entries itself and the server has never heard of them.
//
// No client-side dedupe (deliberately no module-level state): callers
// run this on transcript-entry identity changes only, so a broken
// transcript costs a few events per load, and Sentry groups them.
export const reportMissingExcerptDetails = (queryClient, transcriptData) => {
  const excerptIds = transcriptData?.excerpt_ids;
  if (!Array.isArray(excerptIds) || !excerptIds.length) return;
  const missing = excerptIds.filter(
    (id) => typeof id === 'number' && queryClient.getQueryData(excerptKeys.detail(id)) === undefined
  );
  if (!missing.length) return;
  Sentry.captureMessage('Transcript membership has excerpt ids with no detail entry', {
    level: 'warning',
    extra: {
      transcriptId: transcriptData.id,
      projectId: transcriptData.project_id,
      memberCount: excerptIds.length,
      missingCount: missing.length,
      missingIds: missing.slice(0, 50),
    },
  });
};

/**
 * Hook to get excerpt data by ID from cache only
 *
 * ⚠️ THIS IS THE SINGLE SOURCE OF TRUTH FOR EXCERPT DATA ⚠️
 *
 * Always use this hook when you need current excerpt data, especially for:
 * - codings (can change when codes are merged/deleted)
 * - memos (can be added/removed)
 * - updated_at (changes with any modification)
 *
 * @param {string|number} excerptId - The ID of the excerpt to fetch from cache
 * @returns {Object} The cached excerpt data or undefined if not found
 */
export function useExcerpt(excerptId) {
  const queryClient = useQueryClient();
  
  const isUuid = excerptId && typeof excerptId === 'string' && excerptId.includes('-');
  const normalizedExcerptId = isUuid || !excerptId ? excerptId : parseInt(excerptId, 10);
  
  // Debug the exact cache key and data
  const cacheKey = excerptKeys.detail(normalizedExcerptId);
  const directCacheData = queryClient.getQueryData(cacheKey);
  
  const result = useQuery({
    queryKey: cacheKey,
    queryFn: () => {
      console.log('❌ queryFn should not be called');
      return Promise.resolve(); // This should never resolve since we're cache-only
    },
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
    // ... other options
  });

  return result;
}

/**
 * Get excerpt IDs for a specific paragraph
 * @param {string|number} codeableId - The codeable item ID (transcript or survey answer)
 * @param {string} codeableType - The type of codeable item ('Transcript' or 'SurveyAnswer')
 * @param {number} paragraphStart - Start position of the paragraph
 * @param {number} paragraphEnd - End position of the paragraph
 */
export function useParagraphExcerptIds(codeableId, codeableType = 'Transcript', paragraphStart, paragraphEnd) {
  return useQuery({
    queryKey: excerptKeys.paragraphExcerptIds(codeableId, codeableType, paragraphStart, paragraphEnd),
    queryFn: () => {
      // This should be populated by initialization or other queries
      throw new Error('Paragraph excerpt IDs should be initialized from transcript data');
    },
    enabled: false,
    initialData: [],
  });
}

// One observer spec for cache-only excerpt-detail resolution, shared by
// useTranscriptExcerpts and useParagraphExcerpts so the two cannot drift:
// detail entries are seeded by the transcript/list queryFns and the
// mutations — NEVER fetched through these observers (enabled: false, with a
// rejecting queryFn to fail loudly if that ever changes). Mounted observers
// also pin the entries against garbage collection.
export const buildExcerptDetailQueries = (excerptIds) =>
  (excerptIds || []).map((id) => ({
    queryKey: excerptKeys.detail(id),
    queryFn: () => Promise.reject(new Error('excerpt detail entries are seeded, not fetched')),
    enabled: false,
    staleTime: Infinity,
  }));

// Module-level combine keeps a stable function identity across renders —
// react-query caches the combined result on it, so a re-render without a
// data change costs no combine pass, and the output array is structurally
// shared (stable identity for consumers' memos).
export const combineExcerptDetails = (results) =>
  results.map((result) => result.data).filter(Boolean);

/**
 * Get actual excerpt objects for a paragraph — the per-paragraph analogue
 * of useTranscriptExcerpts, on the same shared observer spec.
 * @param {string|number} codeableId - The codeable item ID (transcript or survey answer)
 * @param {string} codeableType - The type of codeable item ('Transcript' or 'SurveyAnswer')
 * @param {number} paragraphStart - Start position of the paragraph
 * @param {number} paragraphEnd - End position of the paragraph
 */
export function useParagraphExcerpts(codeableId, codeableType = 'Transcript', paragraphStart, paragraphEnd) {
  const { data: excerptIds } = useParagraphExcerptIds(codeableId, codeableType, paragraphStart, paragraphEnd);

  // Memoized on the ids: react-query keys its option-defaulting and key
  // hashing on the queries array's identity, so an inline map here would
  // redo N hashes per consumer render.
  const queries = useMemo(() => buildExcerptDetailQueries(excerptIds), [excerptIds]);
  const excerpts = useQueries({ queries, combine: combineExcerptDetails });

  return { excerpts };
}

/**
 * Hook to fetch excerpts for a specific code with infinite loading.
 *
 * Ids-only: seeds the detail caches and returns excerpt IDS — resolve
 * current data through useExcerpt(id).
 *
 * @param {string|number} codeId - The ID of the code to fetch excerpts for
 * @param {Object} filters - Filter object containing codes, transcripts, surveys, sort, etc.
 * @param {Object} options - Additional query options
 */
export function useCodeExcerpts(codeId, filters = {}, options = {}) {
  const queryClient = useQueryClient();

  const infiniteQuery = useInfiniteQuery({
    queryKey: excerptKeys.codeInfinite(codeId, filters),
    queryFn: async ({ pageParam = null, signal }) => {
      const response = await QualAPI.getCodeExcerpts(codeId, {
        ...filters,
        limit: 100,
        offset: pageParam
      });
      const data = response.data || response;

      const excerpts = data.excerpts || [];

      // Seed the detail caches — the single store for excerpt objects
      // under the ids-only model. The shared primitive carries the
      // cancelled-fetch guard (see queries.cancellationRace.test.js) and
      // the membership heal.
      seedExcerptDetails(queryClient, excerpts, signal);

      const excerptIds = excerpts.map(excerpt => excerpt.id);

      return {
        excerptIds: excerptIds,
        count: data.count,
        next: data.next,
        hasMore: !!data.next
      };
    },
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.next : undefined,
    enabled: !!codeId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    initialPageParam: null,
    ...options
  });

  // Flatten all excerpt IDs from all pages
  const allExcerptIds = infiniteQuery.data?.pages.flatMap(page => page.excerptIds) || [];
  const totalCount = infiniteQuery.data?.pages[0]?.count || 0;

  // Reset function to clear the infinite query cache
  const reset = useCallback(() => {
    queryClient.resetQueries({ queryKey: excerptKeys.codeInfinite(codeId, filters) });
  }, [codeId, filters, queryClient]);

  return {
    excerptIds: allExcerptIds,
    totalCount,
    isLoading: infiniteQuery.isLoading,
    isFetching: infiniteQuery.isFetching,
    hasNextPage: infiniteQuery.hasNextPage,
    loadMore: infiniteQuery.fetchNextPage,
    error: infiniteQuery.error,
    refetch: infiniteQuery.refetch,
    reset
  };
}

/**
 * Hook to get an invalidate function for code excerpts
 * @param {string|number} codeId - The code ID
 * @param {Object} filters - Filter object
 * @returns {Function} Function to invalidate the code excerpts
 */
export function useInvalidateCodeExcerpts(codeId, filters = {}) {
  const queryClient = useQueryClient();

  return () => {
    const queryKey = excerptKeys.codeInfinite(codeId, filters);
    queryClient.invalidateQueries({ queryKey });
  };
}

/**
 * Hook to fetch filtered excerpts with infinite loading.
 *
 * Ids-only: seeds the detail caches and returns excerpt IDS — resolve
 * current data through useExcerpt(id).
 *
 * @param {string|number} projectId - The project ID
 * @param {Object} filters - Filter object containing codes, transcripts, surveys, etc.
 * @param {string} source - Source type (default: 'snippets')
 * @param {Object} options - Additional query options
 */
export function useFilteredExcerpts(projectId, filters, source = 'snippets', options = {}) {
  const queryClient = useQueryClient();
  
  const infiniteQuery = useInfiniteQuery({
    queryKey: excerptKeys.filtered(projectId, filters, source),
    queryFn: async ({ pageParam = null, signal }) => {
      if (!projectId) return { excerptIds: [], hasMore: false, count: 0 };

      // Use the multi_choices object directly since it's already in the format we need
      // (descriptor ID as the key and array of choice IDs as the value)
      const formattedChoices = filters.multi_choices || {};

      const response = await FilterAPI.filterByCodes(
        projectId,
        filters.codes || [],
        formattedChoices,
        filters.transcripts || [],
        filters.surveys || [],
        100, // limit
        pageParam, // offset
        filters.op,
        filters.sort,
        source,
        filters.required_code_id
      );

      // Fix: The API response is nested under response.data
      const data = response.data || response;
      const excerpts = data.excerpts || [];

      // Shared seeding primitive: cancelled-fetch guard + membership heal.
      seedExcerptDetails(queryClient, excerpts, signal);

      const excerptIds = excerpts.map(excerpt => excerpt.id);

      return {
        excerptIds,
        hasMore: !!data.next,
        nextOffset: data.next,
        count: data.count,
        totalExcerpts: excerptIds.length
      };
    },
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextOffset : undefined,
    enabled: !!projectId,
    initialPageParam: null,
    // stale time 5 seconds
    staleTime: 1000 * 5, // 5 seconds
    gcTime: 1000 * 60 * 10, // 5 minutes
    ...options
  });

  // Flatten all excerpt IDs from pages and get excerpt objects from cache
  const allExcerptIds = infiniteQuery.data?.pages.flatMap(page => page.excerptIds) || [];
  const totalCount = infiniteQuery.data?.pages[0]?.count || 0;
  
  return {
    excerptIds: allExcerptIds,
    totalCount,
    isLoading: infiniteQuery.isLoading,
    isFetching: infiniteQuery.isFetching,
    hasNextPage: infiniteQuery.hasNextPage,
    loadMore: infiniteQuery.fetchNextPage,
    error: infiniteQuery.error
  };
}

/**
 * Hook to get an invalidate function for filtered excerpts
 * This is useful for components that need to trigger a refresh but don't fetch the data themselves
 * @param {string|number} projectId - The project ID
 * @param {Object} filters - Filter object containing codes, transcripts, surveys, etc.
 * @param {string} source - Source type ('snippets' or 'chat')
 * @returns {Function} Function to invalidate the filtered excerpts
 */
export function useInvalidateFilteredExcerpts(projectId, filters, source = 'snippets') {
  const queryClient = useQueryClient();
  
  return () => {
    const queryKey = excerptKeys.filtered(projectId, filters, source);
    queryClient.invalidateQueries({ queryKey });
  };
}

/**
 * Hook to fetch filter report data
 * @param {string|number} projectId - The project ID
 * @param {Object} filters - Filter object containing codes, transcripts, surveys, etc.
 * @param {string} source - Source type (default: 'snippets')
 * @param {Object} options - Additional query options
 * @returns {Object} Filter report data with loading and error states
 */
export function useFilterReport(projectId, filters, source = 'snippets', options = {}) {
  return useQuery({
    queryKey: excerptKeys.filterReport(projectId, filters, source),
    queryFn: async () => {
      if (!projectId) return { codeFrequencies: [], totalExcerpts: 0 };

      const response = await FilterAPI.getFilterReport(
        projectId,
        filters.codes || [],
        filters.multi_choices,
        filters.transcripts || [],
        filters.surveys || [],
        filters.op,
        filters.sort,
        source,
        filters.required_code_id
      );

      // Fix: The API response is nested under response.data
      const data = response.data || response;
      
      // Process the report data
      const report = data.report || {};
      
      // Convert code_ids object to array of code frequencies
      const codeFrequencies = Object.entries(report.code_ids || {}).map(([id, count]) => ({
        id: parseInt(id, 10),
        count
      }));
      
      // Convert transcript_ids object to array of transcript frequencies
      const transcriptFrequencies = Object.entries(report.transcript_ids || {}).map(([id, count]) => ({
        id: parseInt(id, 10),
        count
      }));
      
      // Convert multi_choice_ids object to array of descriptor frequencies
      const multiChoiceFrequencies = Object.entries(report.multi_choice_ids || {}).map(([id, count]) => ({
        id: parseInt(id, 10),
        count
      }));
      
      // Convert survey_ids object to array of survey frequencies
      const surveyFrequencies = Object.entries(report.survey_ids || {}).map(([id, count]) => ({
        id: parseInt(id, 10),
        count
      }));
      
      return {
        codeFrequencies,
        transcriptFrequencies,
        multiChoiceFrequencies,
        surveyFrequencies,
        totalExcerpts: report.excerpt_count || 0
      };
    },
    enabled: !!projectId,
    staleTime: 1000 * 10, // 30 seconds
    gcTime: 1000 * 60 * 5, // 10 minutes
    ...options
  });
}

/**
 * Hook to fetch excerpts in bulk by their IDs
 *
 * ⚠️ WARNING: Returns FULL excerpt objects with potentially stale codings ⚠️
 * For current excerpt data, use useExcerpt(id) instead.
 * Future refactor: Should only return excerpt IDs.
 *
 * @param {string|number} projectId - The project ID
 * @param {Array} excerptIds - Array of excerpt IDs to fetch
 * @param {Object} options - Additional query options
 */
export function useBulkGetExcerpts(projectId, excerptIds = [], options = {}) {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: excerptKeys.bulk(projectId, excerptIds),
    queryFn: async ({ signal }) => {
      if (!projectId || !excerptIds || excerptIds.length === 0) {
        return { excerpts: [], excerptIds: [] };
      }

      const response = await ExcerptAPI.bulkGetExcerpts(projectId, excerptIds);
      const data = response.data || response;
      // A malformed body renders empty; the next fetch gets another go.
      const excerpts = Array.isArray(data.excerpts) ? data.excerpts : [];
      // Shared seeding primitive: cancelled-fetch guard + membership heal.
      seedExcerptDetails(queryClient, excerpts, signal);

      const fetchedExcerptIds = excerpts.map(excerpt => excerpt.id);
      
      return {
        excerpts,
        excerptIds: fetchedExcerptIds
      };
    },
    enabled: !!projectId && Array.isArray(excerptIds) && excerptIds.length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    ...options
  });
}